import React, { useEffect, useState, useCallback } from "react";
import IntlMessages from "@iso/components/utility/intlMessages";
import {
  Col,
  Switch,
  Input,
  Select,
  Button,
  Form,
  Breadcrumb,
  Tooltip,
  message,
} from "antd";
import { ChromePicker } from "react-color";
import { Color, Cover, Popover } from "./Settings.styles";
import SectionTitle from "@iso/components/SectionTitle/SectionTitle";
import { FeedbackModal } from "../../components/Modal/Modal.styles";
import Container from "../../components/Container/Container";
import { getSettings, saveSettings } from "../../services/settings/api";
import { useAuth } from "../../contexts/auth";
import { FormattedMessage, useIntl } from "react-intl";
import alertIcon from "../../assets/images/alert.svg";
import { CaretRightFilled, InfoCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import remarkGfm from "remark-gfm";
import { Markdown } from "../../components/Markdown/index.style";
import PageHeader from "../../components/utility/pageHeader";
import { Modal } from "../../components/Modal";
import { listSandboxKeys } from "../../services/account/api";
import { StartCapture } from "../../components/StartCapture";
import { debounce } from "lodash";

const { Option } = Select;

export default function Settings() {
  const { workspaceId } = useAuth();
  const intl = useIntl();

  const [guidelines, setGuidelines] = useState({
    FRAME_FACE: "",
    GET_CLOSER: "",
    MOVE_AWAY: "",
    GET_EVEN_CLOSER: "",
    OK: "",
  });

  const [messages, setMessages] = useState({
    TITLE: "",
    P: "",
    SPAN: "",
    LIST: "",
    CTA: "",
    TIMEOUT: "",
    OLD_BROWSER: "",
    LOADING: "",
    PORTRAIT: "",
  });

  const [loading, setLoading] = useState(true);
  const [sketchPickerColor, setSketchPickerColor] = useState({
    displayPrimaryColorPicker: false,
    displaySecondaryColorPicker: false,
  });

  const [lang, setLang] = useState("pt");
  const [idCapture, setIdCapture] = useState(false);
  const [instructions, setInstructions] = useState(false);
  const [personalizedMessages, setPersonalizedMessages] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#ffffff");
  const [secondaryColor, setSecondaryColor] = useState("#ffffff");
  const [primaryColorWarning, setPrimaryColorWarning] = useState("");
  const [secondaryColorWarning, setSecondaryColorWarning] = useState("");

  const checkColorLightness = (hexColor, colorType) => {
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    let warningMessage = "";
    if (r + g + b >= 600) {
      const messageId =
        colorType === "primary"
          ? "warning.primary_color_too_light"
          : "warning.secondary_color_too_light";
      warningMessage = intl.formatMessage({ id: messageId });
    }
    if (colorType === "primary") {
      setPrimaryColorWarning(warningMessage);
    } else {
      setSecondaryColorWarning(warningMessage);
    }
  };

  const saveSettingsPartially = async (payload) => {
    await saveSettings(payload, workspaceId);
  };

  const handleLangChange = async (lang) => {
    setLangModalVisible(true);
    setNextLang(lang);
  };

  const handleIdCaptureToggle = async (checked) => {
    setIdCapture(checked);
    await saveSettingsPartially({ capture_id_doc: checked });
  };

  const handleInstructionsToggle = async (checked) => {
    setInstructions(checked);
    await saveSettingsPartially({ instructions: checked });
  };

  const handlePersonalizedMessagesToggle = async (checked) => {
    setPersonalizedMessages(checked);
  };

  const debouncedSavePrimaryColor = useCallback(
    debounce(async (color) => {
      await saveSettingsPartially({ primary_color: color });
    }, 500),
    []
  );

  const debouncedSaveSecondaryColor = useCallback(
    debounce(async (color) => {
      await saveSettingsPartially({ secondary_color: color });
    }, 500),
    []
  );

  const handlePrimaryColorChange = async (color) => {
    setPrimaryColor(color.hex);
    checkColorLightness(color.hex, "primary");
    debouncedSavePrimaryColor(color.hex);
  };

  const handleSecondaryColorChange = async (color) => {
    setSecondaryColor(color.hex);
    checkColorLightness(color.hex, "secondary");
    debouncedSaveSecondaryColor(color.hex);
  };

  const handleChangeGuidelines = async (newGuidelines, blur) => {
    setGuidelines(newGuidelines);
    if (blur === true) {
      await saveSettingsPartially({
        guidelines: JSON.stringify(newGuidelines),
      });
    }
  };

  const handleChangeMessages = async (newMessages, blur) => {
    setMessages(newMessages);
    if (blur === true) {
      await saveSettingsPartially({ messages: JSON.stringify(newMessages) });
    }
  };

  const handleClickPrimary = () => {
    setSketchPickerColor({
      ...sketchPickerColor,
      displayPrimaryColorPicker: !sketchPickerColor.displayPrimaryColorPicker,
    });
  };

  const handleClosePrimary = () => {
    setSketchPickerColor({
      ...sketchPickerColor,
      displayPrimaryColorPicker: false,
    });
    saveSettingsPartially({ primary_color: primaryColor });
  };

  const handleClickSecondary = () => {
    setSketchPickerColor({
      ...sketchPickerColor,
      displaySecondaryColorPicker:
        !sketchPickerColor.displaySecondaryColorPicker,
    });
  };

  const handleCloseSecondary = () => {
    setSketchPickerColor({
      ...sketchPickerColor,
      displaySecondaryColorPicker: false,
    });
    saveSettingsPartially({ secondary_color: secondaryColor });
  };

  const [langModalVisible, setLangModalVisible] = useState(false);

  const [componentInfoModalVisible, setComponentInfoModalVisible] =
    useState(false);
  const [guidelinesInfoModalVisible, setGuidelinesInfoModalVisible] =
    useState(false);
  const [
    personalizedMessagesInfoModalVisible,
    setPersonalizedMessagesInfoModalVisible,
  ] = useState(false);
  const [systemMessagesInfoModalVisible, setSystemMessagesInfoModalVisible] =
    useState(false);

  const [nextLang, setNextLang] = useState("");
  const [modalLangLoading, setModalLangLoading] = useState(false);

  const handleModalLangOk = async () => {
    setModalLangLoading(true);
    await saveSettings({ lang: nextLang }, workspaceId).finally(() => {
      setLang(nextLang);
      setModalLangLoading(false);
      setLangModalVisible(false);
      fetchSettings();
    });
  };

  const handleModalLangCancel = () => {
    setLangModalVisible(false);
  };

  const fetchSettings = async () => {
    const { data } = await getSettings(workspaceId);
    const {
      lang,
      primary_color,
      secondary_color,
      instructions,
      guidelines,
      messages,
      capture_id_doc,
    } = data.message;
    setLang(lang);
    setIdCapture(capture_id_doc);
    setInstructions(instructions);
    setPrimaryColor(primary_color);
    setSecondaryColor(secondary_color);

    if (guidelines) {
      setGuidelines(JSON.parse(guidelines));
    }
    if (messages) {
      setPersonalizedMessages(true);
      setMessages(JSON.parse(messages));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const isActive = (value) => {
    if (value) {
      return "#00ABAB";
    }
    return "rgb(0 0 0 / 25%)";
  };

  if (loading) {
    const antIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />;

    return (
      <Container>
        <PageHeader>
          <div className="flex items-center justify-start">
            <Breadcrumb
              separator={
                <div className="!justify-center !items-center">
                  <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L3.58579 5L0.292893 8.29289C-0.0976315 8.68342 -0.0976315 9.31658 0.292893 9.70711C0.683417 10.0976 1.31658 10.0976 1.70711 9.70711L5.70711 5.70711C6.09763 5.31658 6.09763 4.68342 5.70711 4.29289L1.70711 0.292893C1.31658 -0.097631 0.683418 -0.0976311 0.292893 0.292893Z"
                      fill="#7D7D7D"
                    />
                  </svg>
                </div>
              }
              items={[
                {
                  title: "Settings",
                  className: "!font-normal !pointer-events-none !text-lg",
                },
                {
                  title: "Capture",
                  className: "!text-primary !font-semibold !text-lg",
                },
              ]}
            />
          </div>
        </PageHeader>
        <div className="flex justify-center items-center">
          <Spin size="large" indicator={antIcon} />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <div className="flex items-center justify-start">
          <Breadcrumb
            separator={
              <div className="!justify-center !items-center">
                <svg
                  width="6"
                  height="10"
                  viewBox="0 0 6 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L3.58579 5L0.292893 8.29289C-0.0976315 8.68342 -0.0976315 9.31658 0.292893 9.70711C0.683417 10.0976 1.31658 10.0976 1.70711 9.70711L5.70711 5.70711C6.09763 5.31658 6.09763 4.68342 5.70711 4.29289L1.70711 0.292893C1.31658 -0.097631 0.683418 -0.0976311 0.292893 0.292893Z"
                    fill="#7D7D7D"
                  />
                </svg>
              </div>
            }
            items={[
              {
                title: "Settings",
                className: "!font-normal !pointer-events-none !text-lg",
              },
              {
                title: "Capture",
                className: "!text-primary !font-semibold !text-lg",
              },
            ]}
          />
        </div>
      </PageHeader>
      <div className="grid lg:grid-cols-2 grid-cols-1 gap-5 mt-5">
        <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm">
          <div className="flex justify-between items-center">
            <SectionTitle>
              <IntlMessages id="page.settings.component" />
              <div className="cursor-pointer">
                <InfoCircleOutlined
                  onClick={() => setComponentInfoModalVisible(true)}
                  style={{ fontSize: "20px" }}
                />
              </div>
            </SectionTitle>
            <StartCapture />
          </div>
          <Form layout="vertical">
            <Form.Item
              label={<IntlMessages id="page.settings.instructions_language" />}
            >
              <Select
                onChange={handleLangChange}
                size={"large"}
                defaultValue="pt"
                value={lang}
              >
                <Option value="en">English</Option>
                <Option value="es">Español</Option>
                <Option value="pt">Português</Option>
                <Option value="de">Deutsch</Option>
                <Option value="nl">Nederlands</Option>
                <Option value="fr">Français</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label={<IntlMessages id="page.settings.primary_color" />}
            >
              <Color
                onClick={() => handleClickPrimary()}
                color={primaryColor}
              />
              {primaryColorWarning && (
                <div className="flex items-center mt-1">
                  <ExclamationCircleOutlined className="text-yellow-500 mr-1" />
                  <p className="text-yellow-500 text-xs">{primaryColorWarning}</p>
                </div>
              )}
              {sketchPickerColor.displayPrimaryColorPicker ? (
                <Popover>
                  <Cover onClick={() => handleClosePrimary()} />
                  <ChromePicker
                    disableAlpha={true}
                    color={primaryColor}
                    onChange={handlePrimaryColorChange}
                  />
                </Popover>
              ) : null}
            </Form.Item>
            <Form.Item
              label={<IntlMessages id="page.settings.secondary_color" />}
            >
              <Color
                onClick={() => handleClickSecondary()}
                color={secondaryColor}
              />
              {secondaryColorWarning && (
                <div className="flex items-center mt-1">
                  <ExclamationCircleOutlined className="text-yellow-500 mr-1" />
                  <p className="text-yellow-500 text-xs">{secondaryColorWarning}</p>
                </div>
              )}
              {sketchPickerColor.displaySecondaryColorPicker ? (
                <Popover>
                  <Cover onClick={() => handleCloseSecondary()} />
                  <ChromePicker
                    disableAlpha={true}
                    color={secondaryColor}
                    onChange={handleSecondaryColorChange}
                  />
                </Popover>
              ) : null}
            </Form.Item>
            <Form.Item
              label={
                <IntlMessages id="page.settings.pre_capture_instructions" />
              }
            >
              <Switch
                style={{ backgroundColor: isActive(instructions) }}
                onChange={handleInstructionsToggle}
                checked={instructions}
              />
            </Form.Item>
            <Form.Item label={<IntlMessages id="page.settings.id_capture" />}>
              <Switch
                style={{ backgroundColor: isActive(idCapture) }}
                onChange={handleIdCaptureToggle}
                checked={idCapture}
              />
            </Form.Item>
            <Form.Item
              label={<IntlMessages id="page.settings.personalized_messages" />}
            >
              <Switch
                style={{ backgroundColor: isActive(personalizedMessages) }}
                onChange={handlePersonalizedMessagesToggle}
                checked={personalizedMessages}
              />
            </Form.Item>
          </Form>
        </div>

        <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm">
          <div className="mb-5">
            <SectionTitle>
              <IntlMessages id="page.settings.capture_guideline" />
              <div className="cursor-pointer">
                <InfoCircleOutlined
                  onClick={() => setGuidelinesInfoModalVisible(true)}
                  style={{ fontSize: "20px" }}
                />
              </div>
            </SectionTitle>
          </div>
          <Form layout="vertical">
            <Form.Item label="FRAME_FACE:">
              <Input
                onBlur={(e) =>
                  handleChangeGuidelines(
                    { ...guidelines, FRAME_FACE: e.target.value },
                    true,
                  )
                }
                onChange={(e) =>
                  handleChangeGuidelines({
                    ...guidelines,
                    FRAME_FACE: e.target.value,
                  })
                }
                size={"large"}
                value={guidelines.FRAME_FACE}
              />
            </Form.Item>
            <Form.Item label="GET_CLOSER:">
              <Input
                onBlur={(e) =>
                  handleChangeGuidelines(
                    { ...guidelines, GET_CLOSER: e.target.value },
                    true,
                  )
                }
                onChange={(e) =>
                  handleChangeGuidelines({
                    ...guidelines,
                    GET_CLOSER: e.target.value,
                  })
                }
                size={"large"}
                value={guidelines.GET_CLOSER}
              />
            </Form.Item>
            <Form.Item label="MOVE_AWAY:">
              <Input
                onBlur={(e) =>
                  handleChangeGuidelines(
                    { ...guidelines, MOVE_AWAY: e.target.value },
                    true,
                  )
                }
                onChange={(e) =>
                  handleChangeGuidelines({
                    ...guidelines,
                    MOVE_AWAY: e.target.value,
                  })
                }
                size={"large"}
                value={guidelines.MOVE_AWAY}
              />
            </Form.Item>
            <Form.Item label="GET_EVEN_CLOSER:">
              <Input
                onBlur={(e) =>
                  handleChangeGuidelines(
                    { ...guidelines, GET_EVEN_CLOSER: e.target.value },
                    true,
                  )
                }
                onChange={(e) =>
                  handleChangeGuidelines({
                    ...guidelines,
                    GET_EVEN_CLOSER: e.target.value,
                  })
                }
                size={"large"}
                value={guidelines.GET_EVEN_CLOSER}
              />
            </Form.Item>
            <Form.Item label="OK:">
              <Input
                onBlur={(e) =>
                  handleChangeGuidelines(
                    { ...guidelines, OK: e.target.value },
                    true,
                  )
                }
                onChange={(e) =>
                  handleChangeGuidelines({
                    ...guidelines,
                    OK: e.target.value,
                  })
                }
                size={"large"}
                value={guidelines.OK}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
      {personalizedMessages && (
        <>
          <div className="grid grid-cols-1 gap-5 mt-5">
            <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm">
              <Col span={24}>
                <SectionTitle>
                  <IntlMessages id="page.settings.personalized_messages" />
                  <div className="cursor-pointer">
                    <InfoCircleOutlined
                      onClick={() =>
                        setPersonalizedMessagesInfoModalVisible(true)
                      }
                      style={{ fontSize: "20px" }}
                    />
                  </div>
                </SectionTitle>
              </Col>
              <Form layout="vertical">
                <div className="grid md:grid-cols-3 gap-5">
                  <Form.Item label="TITLE:">
                    <Input
                      onBlur={(e) =>
                        handleChangeMessages(
                          { ...messages, TITLE: e.target.value },
                          true,
                        )
                      }
                      onChange={(e) =>
                        handleChangeMessages({
                          ...messages,
                          TITLE: e.target.value,
                        })
                      }
                      size={"large"}
                      value={messages.TITLE}
                    />
                  </Form.Item>
                  <Form.Item label="P:">
                    <Input
                      onBlur={(e) =>
                        handleChangeMessages(
                          { ...messages, P: e.target.value },
                          true,
                        )
                      }
                      onChange={(e) =>
                        handleChangeMessages({ ...messages, P: e.target.value })
                      }
                      size={"large"}
                      value={messages.P}
                    />
                  </Form.Item>
                  <Form.Item label="SPAN:">
                    <Input
                      onBlur={(e) =>
                        handleChangeMessages(
                          { ...messages, SPAN: e.target.value },
                          true,
                        )
                      }
                      onChange={(e) =>
                        handleChangeMessages({
                          ...messages,
                          SPAN: e.target.value,
                        })
                      }
                      size={"large"}
                      value={messages.SPAN}
                    />
                  </Form.Item>
                  <Form.Item label="CTA:">
                    <Input
                      onBlur={(e) =>
                        handleChangeMessages(
                          { ...messages, CTA: e.target.value },
                          true,
                        )
                      }
                      onChange={(e) =>
                        handleChangeMessages({
                          ...messages,
                          CTA: e.target.value,
                        })
                      }
                      size={"large"}
                      value={messages.CTA}
                    />
                  </Form.Item>
                  <Form.Item
                    label={
                      <div className="flex gap-2">
                        <label>LIST:</label>
                        <Tooltip
                          placement="right"
                          title={
                            <IntlMessages id="page.settings.personalized_messages.instruction_list.tooltip" />
                          }
                        >
                          <div className="cursor-pointer">
                            <InfoCircleOutlined style={{ fontSize: "16px" }} />
                          </div>
                        </Tooltip>
                      </div>
                    }
                  >
                    <Input
                      onBlur={(e) => {
                        handleChangeMessages({ ...messages }, true);
                      }}
                      onChange={(e) => {
                        handleChangeMessages({
                          ...messages,
                          LIST: e.target.value.split(";"),
                        });
                      }}
                      size={"large"}
                      value={messages.LIST.join(";")}
                    />
                  </Form.Item>
                </div>
              </Form>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 mt-5">
            <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm">
              <Col span={24}>
                <SectionTitle>
                  <IntlMessages id="page.settings.system_messages" />
                  <div className="cursor-pointer">
                    <InfoCircleOutlined
                      onClick={() => setSystemMessagesInfoModalVisible(true)}
                      style={{ fontSize: "20px" }}
                    />
                  </div>
                </SectionTitle>
              </Col>
              <Form layout="vertical">
                <div className="grid md:grid-cols-3 gap-5">
                  <Form.Item label="TIMEOUT:">
                    <Input
                      onBlur={(e) =>
                        handleChangeMessages(
                          { ...messages, TIMEOUT: e.target.value },
                          true,
                        )
                      }
                      onChange={(e) =>
                        handleChangeMessages({
                          ...messages,
                          TIMEOUT: e.target.value,
                        })
                      }
                      size={"large"}
                      value={messages.TIMEOUT}
                    />
                  </Form.Item>
                  <Form.Item label="PORTRAIT:">
                    <Input
                      onBlur={(e) =>
                        handleChangeMessages(
                          { ...messages, PORTRAIT: e.target.value },
                          true,
                        )
                      }
                      onChange={(e) =>
                        handleChangeMessages({
                          ...messages,
                          PORTRAIT: e.target.value,
                        })
                      }
                      size={"large"}
                      value={messages.PORTRAIT}
                    />
                  </Form.Item>
                  <Form.Item label="OLD_BROWSER:">
                    <Input
                      onBlur={(e) =>
                        handleChangeMessages(
                          { ...messages, OLD_BROWSER: e.target.value },
                          true,
                        )
                      }
                      onChange={(e) =>
                        handleChangeMessages({
                          ...messages,
                          OLD_BROWSER: e.target.value,
                        })
                      }
                      size={"large"}
                      value={messages.OLD_BROWSER}
                    />
                  </Form.Item>
                  <Form.Item label="LOADING:">
                    <Input
                      onBlur={(e) =>
                        handleChangeMessages(
                          { ...messages, LOADING: e.target.value },
                          true,
                        )
                      }
                      onChange={(e) =>
                        handleChangeMessages({
                          ...messages,
                          LOADING: e.target.value,
                        })
                      }
                      size={"large"}
                      value={messages.LOADING}
                    />
                  </Form.Item>
                </div>
              </Form>
            </div>
          </div>
        </>
      )}
      <Modal.Root visible={langModalVisible}>
        <Modal.Header
          onCancelAction={handleModalLangCancel}
          title={<IntlMessages id="page.settings.lang_modal.title" />}
        />
        <Modal.Icon>
          <img alt="Alert" src={alertIcon} />
        </Modal.Icon>
        <Modal.Content>
          <p className="text-sm text-gray-500">
            <IntlMessages id="page.settings.lang_modal.p" />
          </p>
        </Modal.Content>
        <Modal.Actions
          submitText={<IntlMessages id="confirm" />}
          onSubmitAction={handleModalLangOk}
          onCancelAction={handleModalLangCancel}
        />
      </Modal.Root>
      <FeedbackModal
        title={<IntlMessages id="page.settings.component_info_modal.title" />}
        open={componentInfoModalVisible}
        onCancel={() => setComponentInfoModalVisible(false)}
        footer={null}
      >
        <Markdown
          children={intl.formatMessage({
            id: "page.settings.component_info_modal.p",
          })}
          remarkPlugins={[remarkGfm]}
        />
      </FeedbackModal>
      <FeedbackModal
        title={<IntlMessages id="page.settings.guideline_info_modal.title" />}
        open={guidelinesInfoModalVisible}
        onCancel={() => setGuidelinesInfoModalVisible(false)}
        footer={null}
      >
        <Markdown
          children={intl.formatMessage({
            id: "page.settings.guideline_info_modal.p",
          })}
          remarkPlugins={[remarkGfm]}
        />
      </FeedbackModal>
      <FeedbackModal
        title={
          <IntlMessages id="page.settings.personalized_messages_info_modal.title" />
        }
        open={personalizedMessagesInfoModalVisible}
        onCancel={() => setPersonalizedMessagesInfoModalVisible(false)}
        footer={null}
      >
        <Markdown
          children={intl.formatMessage({
            id: "page.settings.personalized_messages_info_modal.p",
          })}
          remarkPlugins={[remarkGfm]}
        />
      </FeedbackModal>
      <FeedbackModal
        title={
          <IntlMessages id="page.settings.system_messages_info_modal.title" />
        }
        open={systemMessagesInfoModalVisible}
        onCancel={() => setSystemMessagesInfoModalVisible(false)}
        footer={null}
      >
        <Markdown
          children={intl.formatMessage({
            id: "page.settings.system_messages_info_modal.p",
          })}
          remarkPlugins={[remarkGfm]}
        />
      </FeedbackModal>
    </Container>
  );
}
